import type { ImaController } from "../types";
import { supportsGoogleIMA } from "../utils/browserCompat";

declare global {
  interface Window {
    google?: any;
  }
}

export function createImaController(
  video: HTMLVideoElement,
  options?: { continueLiveStreamDuringAds?: boolean }
): ImaController {
  let adPlaying = false;
  let contentVideoHidden = false;
  let originalMutedState = false;
  let originalVolume =
    typeof video.volume === "number" && !Number.isNaN(video.volume)
      ? Math.max(0, Math.min(1, video.volume))
      : 1;
  const listeners = new Map<string, Set<(payload?: any) => void>>();
  const preloadedVast = new Map<string, string>();
  const preloadingVast = new Map<string, Promise<void>>();
  let adVideoElement: HTMLVideoElement | undefined;

  function setAdPlayingFlag(isPlaying: boolean): void {
    if (isPlaying) {
      video.dataset.stormcloudAdPlaying = "true";
    } else {
      delete video.dataset.stormcloudAdPlaying;
    }
  }

  function hideContentVideo(): void {
    if (!contentVideoHidden) {
      video.style.transition = "opacity 0.3s ease-in-out";
      video.style.opacity = "0";
      setTimeout(() => {
        video.style.visibility = "hidden";
      }, 300);
      video.muted = true;
      video.volume = 0;
      contentVideoHidden = true;
    }
  }

  function showContentVideo(): void {
    if (contentVideoHidden) {
      video.style.visibility = "visible";
      video.style.transition = "opacity 0.3s ease-in-out";
      video.offsetHeight;
      video.style.opacity = "1";
      video.muted = originalMutedState;
      video.volume = originalVolume;
      contentVideoHidden = false;
    }
  }

  function createAdVideoElement(): HTMLVideoElement {
    const adVideo = document.createElement("video");
    adVideo.style.position = "absolute";
    adVideo.style.top = "0";
    adVideo.style.left = "0";
    adVideo.style.width = "100%";
    adVideo.style.height = "100%";
    adVideo.style.objectFit = "contain";
    adVideo.style.backgroundColor = "transparent";
    adVideo.style.zIndex = "15";
    adVideo.playsInline = true;
    adVideo.muted = false;
    adVideo.volume = originalMutedState ? 0 : originalVolume;

    adVideo.style.opacity = "0";
    adVideo.addEventListener(
      "canplay",
      () => {
        adVideo.style.opacity = "1";
      },
      { once: true }
    );

    return adVideo;
  }

  function emit(event: string, payload?: any): void {
    const set = listeners.get(event);
    if (!set) return;
    for (const fn of Array.from(set)) {
      try {
        fn(payload);
      } catch {}
    }
  }

  function ensureImaLoaded(): Promise<void> {
    if (!supportsGoogleIMA()) {
      console.warn(
        "[IMA] Google IMA SDK is not supported on this browser. Please use HLS ad player instead."
      );
      return Promise.reject(
        new Error("Google IMA SDK not supported on this browser")
      );
    }

    try {
      const frameEl = window.frameElement as HTMLIFrameElement | null;
      const sandboxAttr = frameEl?.getAttribute?.("sandbox") || "";
      if (sandboxAttr) {
        const tokens = new Set(
          sandboxAttr
            .split(/\s+/)
            .map((t) => t.trim())
            .filter((t) => t.length > 0)
        );
        const allowsScripts = tokens.has("allow-scripts");
        if (!allowsScripts) {
          // eslint-disable-next-line no-console
          console.error(
            "StormcloudVideoPlayer: The host page is inside a sandboxed iframe without 'allow-scripts'. Google IMA cannot run ads within sandboxed frames. Remove the sandbox attribute or include 'allow-scripts allow-same-origin'."
          );
        }
      }
    } catch {}

    if (typeof window !== "undefined" && window.google?.ima)
      return Promise.resolve();
    const existing = document.querySelector(
      'script[data-ima="true"]'
    ) as HTMLScriptElement | null;
    if (existing) {
      if (window.google?.ima) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("IMA SDK load timeout"));
        }, 10000);
        existing.addEventListener("load", () => {
          clearTimeout(timeout);
          resolve();
        });
        existing.addEventListener("error", () => {
          clearTimeout(timeout);
          reject(new Error("IMA SDK load failed"));
        });
      });
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://imasdk.googleapis.com/js/sdkloader/ima3.js";
      script.async = true;
      script.defer = true;
      script.setAttribute("data-ima", "true");
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("IMA SDK load failed"));
      document.head.appendChild(script);
    });
  }

  let adsManager: any | undefined;
  let adsLoader: any | undefined;
  let adDisplayContainer: any | undefined;
  let adContainerEl: HTMLDivElement | undefined;
  let lastAdTagUrl: string | undefined;
  let retryAttempts = 0;
  const maxRetries = 2;
  const backoffBaseMs = 500;
  let adsLoadedPromise: Promise<void> | undefined;
  let adsLoadedResolve: (() => void) | undefined;
  let adsLoadedReject: ((error: Error) => void) | undefined;

  function makeAdsRequest(google: any, vastTagUrl: string) {
    const adsRequest = new google.ima.AdsRequest();
    
    console.log("[IMA] 📡 Requesting VAST from URL (letting IMA fetch fresh)");
    adsRequest.adTagUrl = vastTagUrl;

    const videoWidth = video.offsetWidth || video.clientWidth || 640;
    const videoHeight = video.offsetHeight || video.clientHeight || 360;

    adsRequest.linearAdSlotWidth = videoWidth;
    adsRequest.linearAdSlotHeight = videoHeight;
    adsRequest.nonLinearAdSlotWidth = videoWidth;
    adsRequest.nonLinearAdSlotHeight = videoHeight;

    if (typeof adsRequest.setAdWillAutoPlay === "function") {
      try {
        const willAutoPlay = !video.paused || video.autoplay;
        adsRequest.setAdWillAutoPlay(willAutoPlay);
      } catch (error) {
        console.warn("[IMA] Failed to call setAdWillAutoPlay:", error);
      }
    }

    if (typeof adsRequest.setAdWillPlayMuted === "function") {
      try {
        const willPlayMuted = video.muted || video.volume === 0;
        adsRequest.setAdWillPlayMuted(willPlayMuted);
      } catch (error) {
        console.warn("[IMA] Failed to call setAdWillPlayMuted:", error);
      }
    }

    adsRequest.vastLoadTimeout = 10000;

    adsLoader.requestAds(adsRequest);
  }

  function ensurePlaceholderContainer(): void {
    if (adContainerEl) {
      return;
    }

    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "0";
    container.style.top = "0";
    container.style.right = "0";
    container.style.bottom = "0";
    container.style.display = "none";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.pointerEvents = "none";
    container.style.zIndex = "10";
    container.style.backgroundColor = "transparent";
    container.style.transition =
      "opacity 0.3s ease-in-out, background-color 0.3s ease-in-out";
    container.style.opacity = "0";

    video.parentElement?.appendChild(container);
    adContainerEl = container;
  }

  async function fetchVastDocument(vastTagUrl: string): Promise<string> {
    const response = await fetch(vastTagUrl, { mode: "cors" });
    if (!response.ok) {
      throw new Error(`Failed to preload VAST: ${response.status}`);
    }
    return response.text();
  }

  function destroyAdsManager() {
    if (adsManager) {
      try {
        adsManager.destroy();
      } catch {}
      adsManager = undefined;
    }

    if (adVideoElement) {
      adVideoElement.style.opacity = "0";
    }
  }

  return {
    initialize() {
      ensureImaLoaded()
        .then(() => {
          const google = window.google;
          ensurePlaceholderContainer();

          if (!adDisplayContainer && adContainerEl) {
            if (!adVideoElement) {
              adVideoElement = createAdVideoElement();
              adContainerEl.appendChild(adVideoElement);
            }

            adDisplayContainer = new google.ima.AdDisplayContainer(
              adContainerEl,
              adVideoElement
            );
            try {
              adDisplayContainer.initialize?.();
            } catch {}
          }
        })
        .catch(() => {});
    },
    async requestAds(vastTagUrl: string) {
      if (!vastTagUrl || vastTagUrl.trim() === "") {
        const error = new Error("VAST tag URL is empty or undefined");
        console.warn("[IMA] ❌", error.message);
        return Promise.reject(error);
      }

      try {
        new URL(vastTagUrl);
      } catch (e) {
        const error = new Error(`Invalid VAST tag URL format: ${vastTagUrl}`);
        console.warn("[IMA] ❌", error.message);
        return Promise.reject(error);
      }

      if (adPlaying) {
        console.warn(
          "[IMA] ⚠️ Cannot request new ads while an ad is playing. Call stop() first."
        );
        return Promise.reject(
          new Error("Ad already playing - cannot request new ads")
        );
      }

      destroyAdsManager();

      adsLoadedReject = undefined;
      adsLoadedResolve = undefined;

      let currentReject: ((error: Error) => void) | undefined;
      adsLoadedPromise = new Promise<void>((resolve, reject) => {
        adsLoadedResolve = resolve;
        adsLoadedReject = reject;
        currentReject = reject;

        setTimeout(() => {
          if (adsLoadedReject) {
            adsLoadedReject(new Error("Ad request timeout"));
            adsLoadedReject = undefined;
            adsLoadedResolve = undefined;
          }
        }, 10000);
      });

      try {
        await ensureImaLoaded();
        const google = window.google;
        lastAdTagUrl = vastTagUrl;
        retryAttempts = 0;

        if (!adDisplayContainer) {
          const container = document.createElement("div");
          container.style.position = "absolute";
          container.style.left = "0";
          container.style.top = "0";
          container.style.right = "0";
          container.style.bottom = "0";
          container.style.display = "none";
          container.style.alignItems = "center";
          container.style.justifyContent = "center";
          container.style.pointerEvents = "none";
          container.style.zIndex = "10";
          container.style.backgroundColor = "transparent";
          container.style.transition =
            "opacity 0.3s ease-in-out, background-color 0.3s ease-in-out";
          container.style.opacity = "0";

          if (!video.parentElement) {
            throw new Error("Video element has no parent for ad container");
          }

          video.parentElement.appendChild(container);
          adContainerEl = container;

          if (!adVideoElement) {
            adVideoElement = createAdVideoElement();
            adContainerEl.appendChild(adVideoElement);
          }

          adDisplayContainer = new google.ima.AdDisplayContainer(
            container,
            adVideoElement
          );

        }

        const videoWidth = video.offsetWidth || video.clientWidth;
        const videoHeight = video.offsetHeight || video.clientHeight;

        if (
          !videoWidth ||
          !videoHeight ||
          videoWidth === 0 ||
          videoHeight === 0
        ) {
          const error = new Error(
            `Invalid video dimensions: ${videoWidth}x${videoHeight}. Cannot initialize ads.`
          );
          console.warn("[IMA]", error.message);
          currentReject?.(error);
          adsLoadedReject = undefined;
          adsLoadedResolve = undefined;
          return Promise.reject(error);
        }

        if (!adsLoader) {
          const adsLoaderCls = new google.ima.AdsLoader(adDisplayContainer);
          adsLoader = adsLoaderCls;

          adsLoader.addEventListener(
            google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
            (evt: any) => {
              try {
                const adsRenderingSettings =
                  new google.ima.AdsRenderingSettings();
                adsRenderingSettings.enablePreloading = true;
                adsManager = evt.getAdsManager(video, adsRenderingSettings);
                const AdEvent = google.ima.AdEvent.Type;
                const AdErrorEvent = google.ima.AdErrorEvent.Type;

                adsManager.addEventListener(
                  AdErrorEvent.AD_ERROR,
                  (errorEvent: any) => {
                    const error = errorEvent.getError();
                    console.error("[DEBUG-ERROR] ❌ AD_ERROR:", error.getMessage?.());

                    destroyAdsManager();

                    adPlaying = false;
                    setAdPlayingFlag(false);

                    if (adContainerEl) {
                      adContainerEl.style.opacity = "0";
                      adContainerEl.style.backgroundColor = "transparent";
                      setTimeout(() => {
                        if (adContainerEl) {
                          adContainerEl.style.pointerEvents = "none";
                          adContainerEl.style.display = "none";
                        }
                      }, 300);
                    }

                    showContentVideo();

                    if (adsLoadedReject) {
                      adsLoadedReject(new Error("Ad playback error"));
                      adsLoadedReject = undefined;
                      adsLoadedResolve = undefined;
                    }

                    if (lastAdTagUrl && retryAttempts < maxRetries) {
                      const delay =
                        backoffBaseMs * Math.pow(2, retryAttempts++);
                      window.setTimeout(() => {
                        try {
                          makeAdsRequest(google, lastAdTagUrl!);
                        } catch {}
                      }, delay);
                    } else {
                      emit("ad_error");

                      if (!options?.continueLiveStreamDuringAds) {
                        if (video.paused) {
                          video.play()?.catch(() => {});
                        }
                      }
                    }
                  }
                );

                adsManager.addEventListener(
                  AdEvent.CONTENT_PAUSE_REQUESTED,
                  () => {
                    if (!options?.continueLiveStreamDuringAds) {
                      video.pause();
                    }

                    adPlaying = true;
                    setAdPlayingFlag(true);
                    
                    emit("content_pause");
                  }
                );

                adsManager.addEventListener(AdEvent.STARTED, () => {
                  setAdPlayingFlag(true);

                  hideContentVideo();

                  if (adVideoElement) {
                    adVideoElement.volume = originalMutedState
                      ? 0
                      : originalVolume;
                    adVideoElement.muted = originalMutedState;
                  }

                  if (adContainerEl) {
                    adContainerEl.style.pointerEvents = "auto";
                    adContainerEl.style.display = "flex";
                    adContainerEl.style.backgroundColor = "#000";
                    adContainerEl.offsetHeight;
                    adContainerEl.style.opacity = "1";
                  }
                });

                adsManager.addEventListener(
                  AdEvent.CONTENT_RESUME_REQUESTED,
                  () => {
                    adPlaying = false;
                    setAdPlayingFlag(false);
                    
                    emit("content_resume");
                  }
                );

                adsManager.addEventListener(AdEvent.ALL_ADS_COMPLETED, () => {
                  adPlaying = false;
                  setAdPlayingFlag(false);

                  if (adContainerEl) {
                    adContainerEl.style.opacity = "0";
                    adContainerEl.style.backgroundColor = "transparent";
                    setTimeout(() => {
                      if (adContainerEl) {
                        adContainerEl.style.pointerEvents = "none";
                        adContainerEl.style.display = "none";
                      }
                    }, 300);
                  }

                  showContentVideo();

                  if (!options?.continueLiveStreamDuringAds && video.paused) {
                    video.play().catch(() => {});
                  }

                  emit("all_ads_completed");
                });

                if (adsLoadedResolve) {
                  adsLoadedResolve();
                  adsLoadedResolve = undefined;
                  adsLoadedReject = undefined;
                }
              } catch (e) {
                console.error("[IMA] Error setting up ads manager:", e);
                  adPlaying = false;
                  setAdPlayingFlag(false);
                if (adContainerEl) {
                  adContainerEl.style.opacity = "0";
                  adContainerEl.style.backgroundColor = "transparent";
                  setTimeout(() => {
                    if (adContainerEl) {
                      adContainerEl.style.pointerEvents = "none";
                      adContainerEl.style.display = "none";
                    }
                  }, 300);
                }
                showContentVideo();

                if (!options?.continueLiveStreamDuringAds) {
                  if (video.paused) {
                    video.play().catch(() => {});
                  }
                }

                if (adsLoadedReject) {
                  adsLoadedReject(new Error("Failed to setup ads manager"));
                  adsLoadedReject = undefined;
                  adsLoadedResolve = undefined;
                }
                emit("ad_error");
              }
            },
            false
          );

          adsLoader.addEventListener(
            google.ima.AdErrorEvent.Type.AD_ERROR,
            (adErrorEvent: any) => {
              const error = adErrorEvent.getError();
              console.error("[DEBUG-ERROR] ❌ ADS_LOADER ERROR:", error.getMessage?.());

              adPlaying = false;
              setAdPlayingFlag(false);

              if (adContainerEl) {
                adContainerEl.style.opacity = "0";
                adContainerEl.style.backgroundColor = "transparent";
                setTimeout(() => {
                  if (adContainerEl) {
                    adContainerEl.style.pointerEvents = "none";
                    adContainerEl.style.display = "none";
                  }
                }, 300);
              }

              showContentVideo();

              if (!options?.continueLiveStreamDuringAds) {
                if (video.paused) {
                  video.play().catch(() => {});
                }
              }

              if (adsLoadedReject) {
                adsLoadedReject(new Error("Ads loader error"));
                adsLoadedReject = undefined;
                adsLoadedResolve = undefined;
              }
              emit("ad_error");
            },
            false
          );
        }

        makeAdsRequest(google, vastTagUrl);
        return adsLoadedPromise;
      } catch (error) {
        console.error("[IMA] Failed to request ads:", error);

        currentReject?.(error as Error);
        adsLoadedReject = undefined;
        adsLoadedResolve = undefined;
        return Promise.reject(error);
      }
    },
    async preloadAds(vastTagUrl: string) {
      if (!vastTagUrl || vastTagUrl.trim() === "") {
        return Promise.resolve();
      }

      if (preloadedVast.has(vastTagUrl)) {
        return Promise.resolve();
      }

      const inflight = preloadingVast.get(vastTagUrl);
      if (inflight) {
        return inflight;
      }

      const preloadPromise = fetchVastDocument(vastTagUrl)
        .then((xml) => {
          preloadedVast.set(vastTagUrl, xml);
        })
        .catch(() => {
          preloadedVast.delete(vastTagUrl);
        })
        .finally(() => {
          preloadingVast.delete(vastTagUrl);
        });

      preloadingVast.set(vastTagUrl, preloadPromise);
      return preloadPromise;
    },
    hasPreloadedAd(vastTagUrl: string) {
      return preloadedVast.has(vastTagUrl);
    },
    async play() {
      if (!window.google?.ima || !adDisplayContainer) {
        return Promise.reject(new Error("IMA SDK not available"));
      }

      if (!adsManager) {
        return Promise.reject(new Error("No ads manager"));
      }

      try {
        const width = video.clientWidth || 640;
        const height = video.clientHeight || 360;

        adsManager.init(width, height, window.google.ima.ViewMode.NORMAL);

        adPlaying = true;

        const adVolume = originalMutedState ? 0 : originalVolume;
        if (adVideoElement) {
          adVideoElement.volume = adVolume;
          adVideoElement.muted = originalMutedState;
        }

        try {
          adsManager.setVolume(adVolume);
        } catch {}

        adsManager.start();

        return Promise.resolve();
      } catch (error) {
        console.error("[IMA] ❌ Error starting ad:", error);
        adPlaying = false;
        setAdPlayingFlag(false);

        if (!options?.continueLiveStreamDuringAds) {
          video.play()?.catch(() => {});
        }
        return Promise.reject(error);
      }
    },
    async stop() {
      adPlaying = false;
      setAdPlayingFlag(false);

      if (adContainerEl) {
        adContainerEl.style.opacity = "0";
        adContainerEl.style.backgroundColor = "transparent";
        setTimeout(() => {
          if (adContainerEl) {
            adContainerEl.style.pointerEvents = "none";
            adContainerEl.style.display = "none";
          }
        }, 300);
      }

      showContentVideo();

      try {
        adsManager?.stop?.();
      } catch {}

      destroyAdsManager();
    },
    destroy() {
      destroyAdsManager();

      adPlaying = false;
      setAdPlayingFlag(false);

      if (adContainerEl) {
        adContainerEl.style.opacity = "0";
        adContainerEl.style.backgroundColor = "transparent";
        setTimeout(() => {
          if (adContainerEl) {
            adContainerEl.style.pointerEvents = "none";
            adContainerEl.style.display = "none";

            if (adContainerEl.parentElement) {
              adContainerEl.parentElement.removeChild(adContainerEl);
            }

            adContainerEl = undefined;
            adVideoElement = undefined;
          }
        }, 300);
      }

      showContentVideo();

      try {
        adsLoader?.destroy?.();
      } catch {}

      adDisplayContainer = undefined;
      adsLoader = undefined;
      contentVideoHidden = false;
      preloadedVast.clear();
      preloadingVast.clear();
    },
    isAdPlaying() {
      return adPlaying;
    },
    resize(width: number, height: number) {
      if (!adsManager || !window.google?.ima) {
        console.warn(
          "[IMA] Cannot resize: No ads manager or IMA SDK available"
        );
        return;
      }

      try {
        console.log(`[IMA] Resizing ads manager to ${width}x${height}`);
        adsManager.resize(width, height, window.google.ima.ViewMode.NORMAL);
      } catch (error) {
        console.warn("[IMA] Error resizing ads manager:", error);
      }
    },
    on(event: string, listener: (payload?: any) => void) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(listener);
    },
    off(event: string, listener: (payload?: any) => void) {
      listeners.get(event)?.delete(listener);
    },
    updateOriginalMutedState(muted: boolean, volume?: number) {
      const nextVolume =
        typeof volume === "number" && !Number.isNaN(volume)
          ? Math.max(0, Math.min(1, volume))
          : originalVolume;
      originalMutedState = muted;
      originalVolume = nextVolume;
    },
    getOriginalMutedState() {
      return originalMutedState;
    },
    getOriginalVolume() {
      return originalVolume;
    },
    setAdVolume(volume: number) {
      const clampedVolume = Math.max(0, Math.min(1, volume));

      if (adVideoElement && adPlaying) {
        adVideoElement.volume = clampedVolume;
        adVideoElement.muted = clampedVolume === 0;
      }

      if (adsManager && adPlaying) {
        try {
          adsManager.setVolume(clampedVolume);
        } catch {}
      }
    },
    getAdVolume(): number {
      if (adVideoElement && adPlaying) {
        return adVideoElement.volume;
      }

      if (adsManager && adPlaying) {
        try {
          return adsManager.getVolume();
        } catch (error) {
          console.warn("[IMA] Failed to get ad volume:", error);
          return 1;
        }
      }
      return 1;
    },
    showPlaceholder() {
      ensurePlaceholderContainer();
      if (adContainerEl) {
        adContainerEl.style.display = "flex";
        adContainerEl.style.backgroundColor = "#000";
        adContainerEl.offsetHeight;
        adContainerEl.style.opacity = "1";
        adContainerEl.style.pointerEvents = "auto";
      }
    },
    hidePlaceholder() {
      if (adContainerEl) {
        adContainerEl.style.opacity = "0";
        adContainerEl.style.backgroundColor = "transparent";
        setTimeout(() => {
          if (adContainerEl) {
            adContainerEl.style.display = "none";
            adContainerEl.style.pointerEvents = "none";
          }
        }, 300);
      }
    },
  };
}
